import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { requestApplyUserAction } from 'api/operations/enrollmentService';
import { resetProducerAnswers } from 'api/operations/formService';
import { requestAgentsAndAssignees, transferGroupV2 } from 'api/operations/groupService';
import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { SearchAgencyDelegate } from 'store/pages/tools/bi-groups-dashboard/searchAgencyDelegate';
import { GroupAssigneeOption, SelectedGroup } from 'store/pages/tools/bi-groups-dashboard/types';
import { FullAgency } from 'types/aaaService';
import { EnrollmentStage, USER_ACTIONS } from 'types/enrollmentService';
import { InputAssignee } from 'types/groupService';

interface CreationParams {
  updatePageData: () => Promise<void>;
  getAllSponsorAgencies: () => FullAgency[];
  getSelectedGroup: () => SelectedGroup;
  closeModal: () => void;
}

interface FormFields extends DefaultFormFields {
  agencyId: FormControl<string[]>;
  assigneeIds: FormControl<string[]>;
}

export default class MoveGroupModalStore {
  private _updatePageData: () => Promise<void>;
  private _isAssigneesSectionLoading = false;
  private _assigneeOptions: GroupAssigneeOption[] = [];
  private _getSelectedGroup: () => SelectedGroup;
  private _closeModal: () => void;

  private _getFormattedAssignees(assigneeIds: string[]): InputAssignee[] {
    const selectedIds = new Set(assigneeIds);
    return this._assigneeOptions
      .filter((assignee) => selectedIds.has(assignee.id))
      .map(({ id, name }) => ({ id, name }));
  }

  private async _moveGroup(): Promise<void> {
    const { groupId, agencyId, assignees, enrollingPeriodIds } = runInAction(() => ({
      groupId: this._getSelectedGroup().id,
      agencyId: this.form.fields.agencyId.value[0] ?? '',
      assignees: this._getFormattedAssignees(this.form.fields.assigneeIds.value.slice()),
      enrollingPeriodIds: this._getSelectedGroup()
        .enrollments.filter((enrollment) => enrollment.stage === EnrollmentStage.ENROLLING)
        .map((enrollment) => enrollment.id),
    }));

    await transferGroupV2(groupId, agencyId, assignees);

    if (enrollingPeriodIds.length > 0) {
      await Promise.all(
        enrollingPeriodIds.flatMap((periodId) => [
          requestApplyUserAction(periodId, USER_ACTIONS.RESET_PRODUCER_STEP),
          resetProducerAnswers(periodId),
        ]),
      );
    }

    runInAction(() => {
      this._closeModal();
    });
    await this._updatePageData();
  }

  private _setAssigneesLoading(loadingStatus: boolean): void {
    this._isAssigneesSectionLoading = loadingStatus;
  }

  private _setAssigneeOptions(assignees: GroupAssigneeOption[]): void {
    this._assigneeOptions = assignees;
  }

  private _applyAssigneeSelection(agencyAssignees: GroupAssigneeOption[]): void {
    this._setAssigneeOptions(agencyAssignees);

    const assignedIds = this._getSelectedGroup().assigned.map((assignee) => assignee.id);
    this.form.fields.assigneeIds.overrideValue(assignedIds);

    if (this._assigneeOptions.length > 0) {
      this.form.fields.assigneeIds.enableControl();
    } else {
      this.form.fields.assigneeIds.disableControl();
    }
  }

  private async _loadAgencyAssignees(agencyId: string): Promise<void> {
    this._setAssigneesLoading(true);
    this.form.fields.assigneeIds.disableControl();

    const { assignees } = await requestAgentsAndAssignees(agencyId);
    runInAction(() => {
      if ((this.form.fields.agencyId.value[0] ?? '') !== agencyId) {
        return;
      }

      this._applyAssigneeSelection(assignees.map(({ id, name }) => ({ id, name })));
      this._setAssigneesLoading(false);
    });
  }

  public form = new FormController<FormFields>(
    {
      agencyId: new FormControl<string[]>([], { optional: false }),
      assigneeIds: new FormControl<string[]>([], { optional: true, disabled: true }),
    },
    { onSubmit: this._moveGroup.bind(this) },
  );

  public searchAgencyDelegate;

  public get isAssigneesSectionLoading(): boolean {
    return this._isAssigneesSectionLoading;
  }

  public get assigneeOptions(): OptionType[] {
    return this._assigneeOptions.map((assignee) => ({
      value: assignee.id,
      label: assignee.name,
    }));
  }

  public get currentAgencyName(): string {
    return (
      this.searchAgencyDelegate.allSponsorAgencies.find(
        (sponsorAgency) => sponsorAgency.id === this._getSelectedGroup().agencyId,
      )?.name ?? ''
    );
  }

  public resetModalData(): void {
    this.form.resetForm();
    this._assigneeOptions = [];
    this.form.fields.assigneeIds.disableControl();
  }

  constructor({ updatePageData, getAllSponsorAgencies, getSelectedGroup, closeModal }: CreationParams) {
    this._getSelectedGroup = getSelectedGroup;
    this._closeModal = closeModal;
    this.searchAgencyDelegate = new SearchAgencyDelegate({ getAllSponsorAgencies });
    this._updatePageData = updatePageData;

    makeObservable<
      MoveGroupModalStore,
      | '_isAssigneesSectionLoading'
      | '_assigneeOptions'
      | '_moveGroup'
      | '_getFormattedAssignees'
      | '_setAssigneesLoading'
      | '_setAssigneeOptions'
      | '_applyAssigneeSelection'
      | '_loadAgencyAssignees'
    >(this, {
      _isAssigneesSectionLoading: observable,
      _assigneeOptions: observable,
      form: observable,
      searchAgencyDelegate: observable,
      isAssigneesSectionLoading: computed,
      assigneeOptions: computed,
      currentAgencyName: computed,
      _setAssigneesLoading: action.bound,
      _moveGroup: action.bound,
      _getFormattedAssignees: action.bound,
      _setAssigneeOptions: action.bound,
      _applyAssigneeSelection: action.bound,
      _loadAgencyAssignees: action.bound,
      resetModalData: action.bound,
    });

    reaction(
      () => this.form.fields.agencyId.value[0] ?? '',
      (agencyId) => {
        this.form.fields.assigneeIds.overrideValue([]);
        this.form.fields.assigneeIds.disableControl();
        this._setAssigneeOptions([]);

        if (agencyId) {
          void this._loadAgencyAssignees(agencyId);
        } else {
          this._setAssigneesLoading(false);
        }
      },
    );
  }
}
